import React from 'react';
import { Link } from 'react-router-dom';
import Layout from './Layout/Layout';
import ReusableCodeExecutor from './ReusableCodeExecutor';
import CTAButton from './CTAButton';
import { useSeo } from '../utils/seo';

const HybridSeoPage = ({
  title,
  metaDescription,
  heroTitle,
  heroDescription,
  quickGuideTitle = "How It Works",
  seoContent,
  defaultCode = `public class HelloWorld {
    public static void main(String[] args) {
        System.out.println("Hello, World!");
        System.out.println("Welcome to Java Code Executor!");
    }
}`,
  className = ""
}) => {
  useSeo(title, metaDescription);

  return (
    <Layout>
      <div className={`hybrid-seo-page ${className}`}>
        {/* Hero Section */}
        <section className="hero-section">
          <div className="hero-container">
            <h1>{heroTitle}</h1>
            <p className="hero-description">{heroDescription}</p>
          </div>
        </section>

        {/* Editor Section - Above the Fold */}
        <section className="editor-section">
          <div className="editor-container">
            <h2>Try It Now - Write Your Java Code</h2>
            <ReusableCodeExecutor 
              defaultCode={defaultCode}
              className="embedded-executor"
            />
          </div>
        </section>

        {/* Quick Guide Section */}
        <section className="quick-guide-section">
          <div className="guide-container">
            <h2>{quickGuideTitle}</h2>
            <div className="steps-grid">
              <div className="step-card">
                <div className="step-number">1</div>
                <h3>Write Code</h3>
                <p>Type your Java code in the editor above. The editor supports standard Java syntax and provides a clean coding environment.</p>
              </div>
              <div className="step-card">
                <div className="step-number">2</div>
                <h3>Click Run</h3>
                <p>Execute your code with the Run button. Our system compiles and runs your Java program instantly in a secure environment.</p>
              </div>
              <div className="step-card">
                <div className="step-number">3</div>
                <h3>View Output</h3>
                <p>See your program's output immediately with status indicators. Results appear in real-time with clear success or error messages.</p>
              </div>
            </div>
          </div>
        </section>

        {/* SEO Content Section */}
        <section className="seo-content-section">
          <div className="content-container">
            {seoContent}
          </div>
        </section>

        {/* CTA Section */}
        <section className="cta-section">
          <div className="cta-container">
            <h2>Ready to Master Java Programming?</h2>
            <p>Start practicing with our interactive Java playground and improve your coding skills with hands-on experience.</p>
            <div className="cta-buttons">
              <CTAButton to="/skillora">Start Practicing on Skillora</CTAButton>
              <CTAButton to="/skillora/java-playground" primary={false}>Explore Java Playground</CTAButton>
            </div>
          </div>
        </section>

        <style jsx>{`
          .hybrid-seo-page {
            background: #ffffff;
            min-height: 100vh;
          }

          /* Hero Section */
          .hero-section {
            background: linear-gradient(135deg, #4a90e2 0%, #357abd 100%);
            padding: 80px 20px 60px;
            text-align: center;
            color: white;
          }

          .hero-container {
            max-width: 800px;
            margin: 0 auto;
          }

          .hero-section h1 {
            font-size: 2.5rem;
            font-weight: 600;
            margin-bottom: 20px;
            line-height: 1.2;
          }

          .hero-description {
            font-size: 1.2rem;
            margin-bottom: 0;
            opacity: 0.9;
            line-height: 1.6;
            max-width: 600px;
            margin-left: auto;
            margin-right: auto;
          }

          /* Editor Section */
          .editor-section {
            background: #f8f9fa;
            padding: 60px 20px;
            border-bottom: 1px solid #e9ecef;
          }

          .editor-container {
            max-width: 1200px;
            margin: 0 auto;
          }

          .editor-container h2 {
            text-align: center;
            font-size: 2rem;
            margin-bottom: 40px;
            color: #333;
          }

          .embedded-executor {
            background: white;
            border-radius: 12px;
            padding: 30px;
            box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
          }

          /* Quick Guide Section */
          .quick-guide-section {
            background: white;
            padding: 60px 20px;
          }

          .guide-container {
            max-width: 1000px;
            margin: 0 auto;
          }

          .quick-guide-section h2 {
            text-align: center;
            font-size: 2rem;
            margin-bottom: 50px;
            color: #333;
          }

          .steps-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
            gap: 40px;
          }

          .step-card {
            text-align: center;
            padding: 30px 20px;
            background: #f8f9fa;
            border-radius: 12px;
            transition: transform 0.3s ease, box-shadow 0.3s ease;
          }

          .step-card:hover {
            transform: translateY(-5px);
            box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
          }

          .step-number {
            width: 60px;
            height: 60px;
            background: #4a90e2;
            color: white;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 1.5rem;
            font-weight: 700;
            margin: 0 auto 20px;
          }

          .step-card h3 {
            font-size: 1.3rem;
            margin-bottom: 15px;
            color: #333;
          }

          .step-card p {
            color: #666;
            line-height: 1.6;
            margin: 0;
          }

          /* SEO Content Section */
          .seo-content-section {
            background: #f8f9fa;
            padding: 60px 20px;
          }

          .content-container {
            max-width: 800px;
            margin: 0 auto;
          }

          .seo-content-section h1 {
            color: #333;
            font-size: 2.5rem;
            margin-bottom: 30px;
            text-align: center;
          }

          .seo-content-section h2 {
            color: #333;
            font-size: 1.8rem;
            margin: 40px 0 20px 0;
            padding-bottom: 10px;
            border-bottom: 2px solid #4a90e2;
          }

          .seo-content-section h3 {
            color: #333;
            font-size: 1.4rem;
            margin: 30px 0 15px 0;
          }

          .seo-content-section p {
            color: #666;
            font-size: 1.1rem;
            line-height: 1.8;
            margin-bottom: 20px;
          }

          .seo-content-section pre {
            background-color: #f8f9fa;
            border: 1px solid #e9ecef;
            border-radius: 8px;
            padding: 20px;
            margin: 20px 0;
            overflow-x: auto;
          }

          .seo-content-section code {
            background-color: #f8f9fa;
            padding: 2px 4px;
            border-radius: 4px;
            font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
            font-size: 0.9rem;
          }

          .seo-content-section pre code {
            background: none;
            padding: 0;
            font-size: 0.9rem;
            line-height: 1.5;
          }

          .seo-content-section a {
            color: #4a90e2;
            text-decoration: none;
            font-weight: 500;
            border-bottom: 1px solid transparent;
            transition: border-color 0.3s ease;
          }

          .seo-content-section a:hover {
            border-bottom-color: #4a90e2;
          }

          /* CTA Section */
          .cta-section {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            padding: 80px 20px;
            text-align: center;
            color: white;
          }

          .cta-container {
            max-width: 800px;
            margin: 0 auto;
          }

          .cta-section h2 {
            font-size: 2.5rem;
            margin-bottom: 20px;
            font-weight: 600;
          }

          .cta-section p {
            font-size: 1.2rem;
            margin-bottom: 40px;
            opacity: 0.9;
            line-height: 1.6;
          }

          .cta-buttons {
            display: flex;
            gap: 20px;
            justify-content: center;
            flex-wrap: wrap;
          }

          /* Responsive Design */
          @media (max-width: 768px) {
            .hero-section h1 {
              font-size: 2rem;
            }
            
            .hero-description {
              font-size: 1.1rem;
            }

            .editor-section {
              padding: 40px 15px;
            }

            .editor-container h2 {
              font-size: 1.5rem;
            }

            .embedded-executor {
              padding: 20px;
            }

            .quick-guide-section {
              padding: 40px 15px;
            }

            .quick-guide-section h2 {
              font-size: 1.5rem;
            }

            .steps-grid {
              grid-template-columns: 1fr;
              gap: 30px;
            }

            .seo-content-section {
              padding: 40px 15px;
            }

            .seo-content-section h1 {
              font-size: 2rem;
            }

            .seo-content-section h2 {
              font-size: 1.5rem;
            }

            .seo-content-section p {
              font-size: 1rem;
            }

            .cta-section {
              padding: 60px 15px;
            }

            .cta-section h2 {
              font-size: 2rem;
            }

            .cta-section p {
              font-size: 1.1rem;
            }

            .cta-buttons {
              flex-direction: column;
              align-items: center;
            }
          }
        `}</style>
      </div>
    </Layout>
  );
};

export default HybridSeoPage;
